import {
  GamePlayData,
  SkillStates,
  CurrencyEnum,
  SkillEnum,
  UpgradeEnum,
  Multiplier,
  MultiplierState,
  EquipmentEnum,
  CurrencyStates,
  MultiplierSourceCategory,
  MultiplierType
} from '@/types'
import { UPGRADES } from '@/constants/upgrades'

function applyMultiplier(
  appliedMultipliers: MultiplierState[] | undefined,
  totalMultiplier: number | undefined,
  sourceCategory: MultiplierSourceCategory,
  sourceName: string,
  value: number,
  category: MultiplierType
): { appliedMultipliers: MultiplierState[]; totalMultiplier: number } {
  const multiplierState: MultiplierState = {
    category,
    sourceCategory,
    sourceName,
    multiplier: value
  }

  return {
    appliedMultipliers: [...(appliedMultipliers || []), multiplierState],
    totalMultiplier: (totalMultiplier || 1) * value
  }
}

function removeMultiplier(
  appliedMultipliers: MultiplierState[] | undefined,
  totalMultiplier: number | undefined,
  sourceCategory: MultiplierSourceCategory,
  sourceName: string
): { appliedMultipliers: MultiplierState[]; totalMultiplier: number } {
  if (!appliedMultipliers) return { appliedMultipliers: [], totalMultiplier: 1 }

  const targetMultipliers = appliedMultipliers.filter(
    (m) => m.sourceCategory === sourceCategory && m.sourceName === sourceName
  )
  const remainingMultipliers = appliedMultipliers.filter(
    (m) => !(m.sourceCategory === sourceCategory && m.sourceName === sourceName)
  )

  if (targetMultipliers.length > 0) {
    const divisionFactor = targetMultipliers.reduce((acc, m) => acc * m.multiplier, 1)
    return {
      appliedMultipliers: remainingMultipliers,
      totalMultiplier: (totalMultiplier || 1) / divisionFactor
    }
  }

  return { appliedMultipliers: remainingMultipliers, totalMultiplier: totalMultiplier || 1 }
}

export function applyMultipliers(
  data: GamePlayData,
  multipliers: Multiplier[],
  sourceName: string,
  sourceCategory: MultiplierSourceCategory
): GamePlayData {
  const newData = { ...data }

  multipliers.forEach((multiplier) => {
    if (multiplier.effectCategory === 'currency') {
      const currency = newData.currencies[multiplier.key as CurrencyEnum]
      const { appliedMultipliers, totalMultiplier } = applyMultiplier(
        currency.appliedMultipliers,
        currency.totalMultiplier,
        sourceCategory,
        sourceName,
        multiplier.value,
        multiplier.type
      )
      newData.currencies[multiplier.key as CurrencyEnum] = {
        ...currency,
        appliedMultipliers,
        totalMultiplier
      }
    } else if (multiplier.effectCategory === 'skill') {
      const skill = newData.skills[multiplier.key as SkillEnum]
      const { appliedMultipliers, totalMultiplier } = applyMultiplier(
        skill.appliedMultipliers,
        skill.totalMultiplier,
        sourceCategory,
        sourceName,
        multiplier.value,
        multiplier.type
      )
      newData.skills[multiplier.key as SkillEnum] = {
        ...skill,
        appliedMultipliers,
        totalMultiplier
      }
    }
  })

  return newData
}

export function removeMultipliers(
  data: GamePlayData,
  sourceCategory: MultiplierSourceCategory,
  sourceName: string
): GamePlayData {
  const newData = { ...data }

  Object.keys(newData.currencies).forEach((key) => {
    const currency = newData.currencies[key as CurrencyEnum]
    const { appliedMultipliers, totalMultiplier } = removeMultiplier(
      currency.appliedMultipliers,
      currency.totalMultiplier,
      sourceCategory,
      sourceName
    )
    newData.currencies[key as CurrencyEnum] = {
      ...currency,
      appliedMultipliers,
      totalMultiplier
    }
  })

  Object.keys(newData.skills).forEach((key) => {
    const skill = newData.skills[key as SkillEnum]
    const { appliedMultipliers, totalMultiplier } = removeMultiplier(
      skill.appliedMultipliers,
      skill.totalMultiplier,
      sourceCategory,
      sourceName
    )
    newData.skills[key as SkillEnum] = {
      ...skill,
      appliedMultipliers,
      totalMultiplier
    }
  })

  return newData
}
